// Which analyses may be linked to a somatic tumour DNA analysis as "the same
// patient", and why.
//
// A link only means something when another part of the platform reads it: the
// matched normal or a germline analysis (germline origin, identity checks) and
// an RNA analysis of the tumour (fusion and expression confirmation). Two tumour
// DNA analyses are not linked here: nothing would merge or compare their
// tables yet. A candidate whose kind cannot be told is kept, never hidden.

export const LINK_KINDS = {
  germline: {
    key: 'germline',
    group: 'Matched normal or germline analysis',
    why: 'Used for germline origin and identity checks.',
  },
  rna: {
    key: 'rna',
    group: 'RNA of this tumour',
    why: 'Used for fusion and expression confirmation.',
  },
  other: {
    key: 'other',
    group: 'Other analyses',
    why: null,
  },
}

export const EXCLUDED_TUMOUR_DNA_TEXT = 'Two tumour DNA analyses are not linked here.'

const COHORT_TAGS = ['MULTI', 'GENESUS-COHORT', 'EXOMEDEPTH-COHORT', 'MILESTONE-COHORT']
const SOMATIC_TAGS = ['Somatic', 'Tumor-Only', 'Tumor/Normal', 'Tumour-only', 'Tumour/Normal']

const tagsOf = (row) => (Array.isArray(row?.tags) ? row.tags.map(String) : [])

// 'germline' | 'rna' | 'somatic_dna' | 'cohort' | 'unknown'
export const analysisKind = (row) => {
  const tags = tagsOf(row)
  const annotation = row?.analysis_parameters?.annotation || {}
  const type = String(row?.analysis_type || '')
  if (tags.some((t) => COHORT_TAGS.includes(t)) || type === 'Multisample' || /COHORT/.test(type)) return 'cohort'
  if (tags.includes('RNA') || /rna/i.test(String(annotation.analyte || annotation.libraryType || ''))) return 'rna'
  const somatic =
    tags.some((t) => SOMATIC_TAGS.includes(t)) ||
    annotation.isSomatic === true ||
    ['TumorOnly', 'TumorNormal'].includes(annotation.analysisType)
  if (somatic) return 'somatic_dna'
  if (tags.includes('Germline') || annotation.analysisType === 'Germline') return 'germline'
  // A germline upload carries no "Somatic" tag; only call it germline when the
  // row is clearly a sample analysis, else leave it unknown (and kept).
  if (tags.includes('VCF') || ['VCF', 'FASTQ', 'ONT_BAM', 'PACBIO_BAM'].includes(type)) return 'germline'
  return 'unknown'
}

// For a somatic tumour DNA analysis: the candidates to offer, each with its
// link kind, plus how many tumour DNA analyses were left out.
export const linkCandidatesForTumour = (rows) => {
  const offered = []
  let excludedTumourDna = 0
  ;(rows || []).forEach((row) => {
    const kind = analysisKind(row)
    if (kind === 'somatic_dna') {
      excludedTumourDna += 1
      return
    }
    if (kind === 'cohort') return
    const linkKind = kind === 'germline' ? LINK_KINDS.germline : kind === 'rna' ? LINK_KINDS.rna : LINK_KINDS.other
    offered.push({ ...row, linkKind })
  })
  const order = { germline: 0, rna: 1, other: 2 }
  offered.sort((a, b) => order[a.linkKind.key] - order[b.linkKind.key])
  return { offered, excludedTumourDna }
}
